import React from 'react';
import { Dialog, DialogContent, Button, Typography } from '@mui/material';
import { Warning as WarningIcon, Close } from '@mui/icons-material';
import {
  LoggerUtility,
  EnumUtility
} from '../components/ComponentsLibrary';

/**
 * SessionTimeoutDialog - Custom dialog for session timeout warnings
 * Uses existing NotificationDialog styling but adds Extend Session functionality
 */
const SessionTimeoutDialog = ({
  open,
  dialogContent,
  onExtendSession,
  onClose
}) => {

  const handleExtendSession = () => {
    try {
      if (onExtendSession) {
        onExtendSession();
      }
      LoggerUtility.LogInfo('SessionTimeoutDialog', 'handleExtendSession', 'User extended session');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutDialog', 'handleExtendSession');
    }
  };

  const handleClose = () => {
    try {
      if (onExtendSession) {
        onExtendSession(); // Same functionality as Extend Session
      }
      LoggerUtility.LogInfo('SessionTimeoutDialog', 'handleClose', 'User closed timeout warning - extending session');
    } catch (error) {
      LoggerUtility.ManageErrorLogging(error, 'SessionTimeoutDialog', 'handleClose');
    }
  };

  if (!open || !dialogContent) return null;

  const styles = {
    dialogContent: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '20px 20px 20px 20px',
      minWidth: '500px',
      position: 'relative'
    },
    iconColumn: {
      flex: '0 0 60px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      marginTop: '5px'
    },
    contentColumn: {
      flex: '1',
      marginLeft: '15px',
      paddingRight: '40px' // Make room for close button
    },
    closeButton: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      minWidth: 'auto',
      padding: '8px',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#ffffff',
      margin: '0 0 15px 0'
    },
    message: {
      fontSize: '16px',
      color: '#ffffff',
      lineHeight: '1.4',
      margin: '0 0 20px 0'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end', // Move button to right side
      gap: '10px'
    },
    extendButton: {
      backgroundColor: 'transparent', // No background - inherits dialog orange
      color: '#ffffff', // White text
      border: '1px solid #ffffff', // Slim white border outline
      padding: '10px 20px',
      fontSize: '16px',
      fontWeight: 'bold',
      textTransform: 'none',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)', // Slight white overlay on hover
        color: '#ffffff'
      }
    },
    iconStyle: {
      fontSize: '40px',
      color: '#ffffff' // White warning icon
    }
  };

  return (
    <Dialog 
      open={open}
      disableEscapeKeyDown={true}
      disableBackdropClick={true}
      classes={{ paper: 'yt-dialog-warning' }}
      PaperProps={{
        sx: {
          minWidth: 700,
          maxWidth: "90%",
          margin: "auto"
        },
      }}
    >
      <DialogContent sx={{ padding: "5px" }}>
        <div style={styles.dialogContent}>
          {/* Warning Icon */}
          <div style={styles.iconColumn}>
            <WarningIcon style={styles.iconStyle} />
          </div>
          
          {/* Content */}
          <div style={styles.contentColumn}>
            <Typography component="h2" style={styles.title}>
              {dialogContent.Title}
            </Typography>
            
            <Typography component="div" style={styles.message}>
              {dialogContent.Message}
            </Typography>
            
            <div style={styles.buttonContainer}>
              <Button
                variant="contained"
                onClick={handleExtendSession}
                sx={styles.extendButton}
              >
                EXTEND SESSION
              </Button>
            </div>
          </div>
          
          {/* Close Button - Positioned absolutely in top right */}
          <Button
            onClick={handleClose}
            sx={styles.closeButton}
          >
            <Close />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeoutDialog;